/**
 * State a mount saves to resume from, moved from the shell's `services/saved-state-service.ts`,
 * `platform/AppStateManager.ts` and the `buildAppSavedState` builder of `logic/app-state-utils.ts`
 * (kernel apps, WS-14 section 3.6; layered shell surfaces, WS-8 section 3.8, R-22).
 *
 * A routed mount persists in `sessionStorage` for an hour, keyed per mount. An embedded or system
 * mount holds its state in memory for the life of the mount and writes no Web Storage key.
 *
 * The keys carry no identity, so the tab records whose saved state it holds under
 * {@link SAVED_STATE_OWNER_KEY}. {@link bindSavedStateOwner} removes every saved-state entry when
 * the session identity differs from that record, so one user never restores another user's
 * state, route or scroll position in the same tab (S2 WS-8 security-2).
 */
import type { FixtureSavedState } from '@zion/sdk-contracts';
import type { SurfaceMountRequest, SurfaceSavedStateService } from '@zion/sdk-surfaces/v1';
import {
  SurfaceServiceRefusedError,
  isPlainJsonObject,
  type MountScope,
  type ServiceSession,
  type ServiceSessionSnapshot,
} from './kit.js';
import { LEGACY_STORAGE_KEYS, STORAGE_KEYS } from './storage-keys.js';

const STATE_PREFIX = STORAGE_KEYS.appStatePrefix;
const LEGACY_STATE_PREFIX = LEGACY_STORAGE_KEYS.appStatePrefix;
/** One hour, the life of a saved-state entry. */
const MAX_AGE_MS = 60 * 60 * 1000;

/**
 * Save an app's state to `sessionStorage` under `STORAGE_KEYS.appStatePrefix` plus `appId`.
 *
 * @throws What `sessionStorage.setItem` throws (quota, private mode). A caller that saves on a
 *   best-effort basis catches it.
 */
export function saveAppState(appId: string, state: FixtureSavedState): void {
  sessionStorage.setItem(`${STATE_PREFIX}${appId}`, JSON.stringify(state));
}

/**
 * Load an app's saved state from `sessionStorage`.
 *
 * - A canonical entry wins. With none, a legacy-prefix entry migrates forward: the call copies it
 *   to the canonical key and removes the legacy key.
 * - No entry, or an empty value, reads `null`.
 * - An entry older than one hour, or one `JSON.parse` refuses, reads `null` and its canonical key
 *   is removed. An entry exactly one hour old is still live.
 *
 * The value is parsed and not shape-checked; a caller that hands it on checks the shape.
 *
 * @throws What `sessionStorage` throws on a refusing store.
 */
export function loadAppState(appId: string): FixtureSavedState | null {
  let raw = sessionStorage.getItem(`${STATE_PREFIX}${appId}`);
  if (raw === null) {
    const legacy = sessionStorage.getItem(`${LEGACY_STATE_PREFIX}${appId}`);
    if (legacy !== null) {
      sessionStorage.setItem(`${STATE_PREFIX}${appId}`, legacy);
      sessionStorage.removeItem(`${LEGACY_STATE_PREFIX}${appId}`);
      raw = legacy;
    }
  }
  if (!raw) return null;
  try {
    const state: FixtureSavedState = JSON.parse(raw);
    if (Date.now() - state.savedAt > MAX_AGE_MS) {
      sessionStorage.removeItem(`${STATE_PREFIX}${appId}`);
      return null;
    }
    return state;
  } catch {
    sessionStorage.removeItem(`${STATE_PREFIX}${appId}`);
    return null;
  }
}

/**
 * Remove every expired or unparseable saved-state entry, canonical and legacy prefix alike.
 * Other keys stay. An index whose key reads `null` and an entry whose value is empty or absent
 * are skipped.
 *
 * @throws What `sessionStorage` throws on a refusing store.
 */
export function clearStaleStates(): void {
  for (let i = sessionStorage.length - 1; i >= 0; i--) {
    const key = sessionStorage.key(i);
    if (!key) continue;
    const isCanonical = key.startsWith(STATE_PREFIX);
    const isLegacy = key.startsWith(LEGACY_STATE_PREFIX);
    if (!isCanonical && !isLegacy) continue;
    const raw = sessionStorage.getItem(key);
    if (!raw) continue;
    try {
      const state: FixtureSavedState = JSON.parse(raw);
      if (Date.now() - state.savedAt > MAX_AGE_MS) sessionStorage.removeItem(key);
    } catch {
      sessionStorage.removeItem(key);
    }
  }
}

/**
 * Build a saved state from the current mount context: `{ route, scrollY, custom, savedAt }`, with
 * `custom` the given data or `{}` and `savedAt` `Date.now()`. Pure apart from the clock read.
 */
export function buildAppSavedState(
  route: string,
  scrollY: number,
  customData?: Record<string, unknown> | void,
): FixtureSavedState {
  return {
    route,
    scrollY,
    custom: customData ?? {},
    savedAt: Date.now(),
  };
}

/**
 * The `sessionStorage` key whose value names the identity that owns the tab's routed saved
 * state: the JSON array `[userId, orgId]` for a signed-in session, the empty string after a
 * sign-out. It is `STORAGE_KEYS.appStateOwner`, outside `STORAGE_KEYS.appStatePrefix`, so
 * {@link clearStaleStates} never reads it (R-80).
 */
export const SAVED_STATE_OWNER_KEY = STORAGE_KEYS.appStateOwner;

/**
 * Make the tab's routed saved state belong to the identity in `snapshot`.
 *
 * @param snapshot The session as the host sees it now.
 * @returns `true` when `snapshot` is authenticated and `sessionStorage` answers, so a routed
 *   mount may read and write its entry. `false` for a hydrating or anonymous snapshot and for a
 *   refusing `sessionStorage`.
 *
 * A hydrating snapshot changes nothing. For an anonymous or authenticated snapshot, when
 * {@link SAVED_STATE_OWNER_KEY} holds another value, or no value, the call removes every key under
 * `STORAGE_KEYS.appStatePrefix` and `LEGACY_STORAGE_KEYS.appStatePrefix`, then records the new
 * owner. A token refresh alone keeps the owner, so it removes nothing. A refusing
 * `sessionStorage` is caught. It never throws.
 */
export function bindSavedStateOwner(snapshot: ServiceSessionSnapshot): boolean {
  if (snapshot.status === 'hydrating') return false;
  const owner = snapshot.status === 'authenticated' ? JSON.stringify([snapshot.userId, snapshot.orgId]) : '';
  try {
    if (sessionStorage.getItem(SAVED_STATE_OWNER_KEY) !== owner) {
      const keys = Array.from({ length: sessionStorage.length }, (_, i) => String(sessionStorage.key(i)));
      for (const key of keys) {
        if (key.startsWith(STATE_PREFIX) || key.startsWith(LEGACY_STATE_PREFIX)) sessionStorage.removeItem(key);
      }
      sessionStorage.setItem(SAVED_STATE_OWNER_KEY, owner);
    }
  } catch {
    return false;
  }
  return snapshot.status === 'authenticated';
}

/**
 * The `sessionStorage` key suffix a mount saves under, after `STORAGE_KEYS.appStatePrefix`.
 *
 * @returns `appId` for a legacy routed mount (`surface: null`), so a resumed legacy app finds its
 *   state. `appId`, `/` and `surface` for a declared routed mount. `null` for an embedded or
 *   system mount, which persists nothing.
 */
export function savedStateKeyFor(request: SurfaceMountRequest): string | null {
  if (request.placement.kind !== 'routed') return null;
  return request.surface === null ? request.appId : `${request.appId}/${request.surface}`;
}

/** True for a stored value with the saved-state shape and a plain JSON `custom`. */
function isSavedState(value: unknown): value is FixtureSavedState {
  if (typeof value !== 'object' || value === null) return false;
  const route: unknown = Reflect.get(value, 'route');
  const scrollY: unknown = Reflect.get(value, 'scrollY');
  const savedAt: unknown = Reflect.get(value, 'savedAt');
  return (
    typeof route === 'string' &&
    typeof scrollY === 'number' &&
    typeof savedAt === 'number' &&
    isPlainJsonObject(Reflect.get(value, 'custom'))
  );
}

/**
 * The mount's {@link SurfaceSavedStateService} (security branch S6).
 *
 * - On a routed mount each call first runs {@link bindSavedStateOwner} with `session.current()`.
 *   When it answers `false` (hydrating, anonymous, or a refusing `sessionStorage`), `restore()`
 *   reads `null` and `save` stores nothing.
 * - `restore()` on a routed mount reads through {@link loadAppState}: an entry over one hour old,
 *   unparseable, of the wrong shape, or a refusing `sessionStorage` reads `null`. A legacy-prefix
 *   entry migrates forward on that read. On an embedded or system mount it returns this mount's
 *   last save, or `null`.
 * - `save(state)` throws `value-invalid` unless `state` is a plain JSON object. Otherwise it
 *   records `{ route: location.pathname, scrollY, custom: state, savedAt }`: routed through
 *   {@link saveAppState}, swallowing a storage refusal; embedded or system in memory.
 * - After the mount ends both members throw `disposed`.
 *
 * @param scope The mount's scope.
 * @param session The host's session port; only a routed mount reads it.
 */
export function createSavedStateService(scope: MountScope, session: ServiceSession): SurfaceSavedStateService {
  const key = savedStateKeyFor(scope.request);
  let memory: FixtureSavedState | null = null;
  return scope.guard({
    restore: (): FixtureSavedState | null => {
      if (key === null) return memory;
      if (!bindSavedStateOwner(session.current())) return null;
      try {
        const stored = loadAppState(key);
        return isSavedState(stored) ? stored : null;
      } catch {
        return null;
      }
    },
    save: (state: Readonly<Record<string, unknown>>): void => {
      if (!isPlainJsonObject(state)) {
        throw new SurfaceServiceRefusedError('value-invalid', 'savedState.save takes a plain JSON object');
      }
      const record = buildAppSavedState(window.location.pathname, window.scrollY, { ...state });
      if (key === null) {
        memory = record;
        return;
      }
      if (!bindSavedStateOwner(session.current())) return;
      try {
        saveAppState(key, record);
      } catch {
        // Storage refuses in private mode and at quota; saving is best-effort.
      }
    },
  });
}
